/**
 * Verivibe SDK - Production-Ready Embeddable Widget
 *
 * Self-contained IIFE bundle with React, Shadow DOM, and Supabase integration.
 * No NPM or build tools required on the host website.
 * Prevents CSS conflicts with isolated Shadow DOM rendering.
 *
 * Usage (paste this script tag on your website):
 * <script
 *   src="https://your-netlify-site.netlify.app/sdk.js"
 *   data-widget-id="your-widget-id"
 *   data-site-id="your-site-id"
 * ></script>
 *
 * User Identification (optional):
 * <script>
 *   // Track users across submissions
 *   window.Verivibe.identify('user-123', 'https://avatar.url');
 * </script>
 */

import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import WidgetRenderer from './components/WidgetRenderer';
import {
  getWidgetById,
  submitVibe,
  identifyUser,
  trackWidgetView,
  VerivibeSDK,
  WidgetWithProject,
} from './services/sdkService';
import { widgetStyles } from './sdk-styles';
import type { ProjectStyling } from './types';

// Global SDK interface
declare global {
  interface Window {
    Verivibe: typeof VerivibeSDK;
    __verovibeWidget?: VerivibeWidget;
    verovibeIdentify?: typeof identifyUser;
  }
}

/**
 * SDK Initialization
 */
/**
 * Consent mode controls whether the SDK collects data before explicit consent.
 * - 'none': (default) Widget loads immediately, no consent prompt. Widget owner
 *   assumes responsibility for consent.
 * - 'external': Widget waits for Verivibe.grantConsent() before rendering,
 *   setting localStorage, or tracking views. Integrates with external consent
 *   managers (OneTrust, CookieBot, etc.).
 */
type ConsentMode = 'none' | 'external';

/** Track global consent state */
let consentGranted = false;
let pendingWidgetInit: (() => void) | null = null;

class VerivibeWidget {
  private shadowRoot: ShadowRoot | null = null;
  private reactRoot: Root | null = null;
  private widget: WidgetWithProject | null = null;
  private projectStyling: ProjectStyling | null = null;
  private widgetId: string;
  private siteId: string | null;
  private containerId: string;
  private consentMode: ConsentMode;

  constructor(widgetId: string, siteId: string | null, consentMode: ConsentMode = 'none') {
    this.widgetId = widgetId;
    this.siteId = siteId;
    this.containerId = `verivibe-widget-${widgetId}`;
    this.consentMode = consentMode;
  }

  /**
   * Initialize the widget
   */
  async init(): Promise<void> {
    console.log('🚀 Verivibe SDK: Starting initialization...', {
      widgetId: this.widgetId,
      siteId: this.siteId,
      containerId: this.containerId,
    });

    try {
      // Fetch widget configuration with project styling from Supabase
      console.log('📡 Verivibe SDK: Fetching widget configuration from Supabase...', {
        widgetId: this.widgetId,
      });

      this.widget = await getWidgetById(this.widgetId);

      if (!this.widget) {
        console.error('❌ Verivibe SDK: Widget not found in database', {
          widgetId: this.widgetId,
        });
        return;
      }

      // Store project styling for dynamic application
      this.projectStyling = this.widget.project || null;

      console.log('✅ Verivibe SDK: Widget configuration loaded', {
        widgetId: this.widget.id,
        type: this.widget.type,
      });

      // Consent gate: in 'external' mode, wait for Verivibe.grantConsent()
      if (this.consentMode === 'external' && !consentGranted) {
        // Check if consent was previously granted (persisted in localStorage)
        const priorConsent = localStorage.getItem(`verivibe_${this.widgetId}_consent`);
        if (priorConsent === 'granted') {
          consentGranted = true;
        } else {
          console.log(
            '🔒 Verivibe SDK: Consent required. Call Verivibe.grantConsent() to activate.'
          );
          pendingWidgetInit = () => this.initAfterConsent();
          return;
        }
      }

      this.initAfterConsent();
    } catch (error) {
      console.error('❌ Verivibe SDK: Initialization FAILED', {
        errorMessage: error instanceof Error ? error.message : String(error),
        widgetId: this.widgetId,
      });
    }
  }

  /**
   * Continue initialization after consent is granted (or if consent mode is 'none')
   */
  private async initAfterConsent(): Promise<void> {
    if (!this.widget) {
      this.widget = await getWidgetById(this.widgetId);
      if (!this.widget) return;
      this.projectStyling = this.widget.project || null;
    }

    try {
      // Create container element
      console.log('📦 Verivibe SDK: Creating container element...');
      const container = this.createContainer();

      // Attach Shadow DOM
      this.shadowRoot = container.attachShadow({ mode: 'open' });

      // Inject styles into Shadow DOM
      this.injectStyles();

      // Create React root mount point
      const mountPoint = document.createElement('div');
      mountPoint.id = 'verivibe-root';
      this.shadowRoot.appendChild(mountPoint);

      // Handle Auto-Show, Initial Delay, and Trigger Frequency logic
      const autoShow = this.widget.config.autoShow ?? true;
      const delaySeconds = this.widget.config.initialDelay || 0;
      const triggerFrequency = this.widget.config.triggerFrequency || 'always';

      // Check trigger frequency BEFORE rendering
      const shouldShow = this.checkTriggerFrequency(triggerFrequency);

      // If frequency check fails, don't render the widget at all
      if (!shouldShow) {
        container.remove();
        return;
      }

      // If autoShow is disabled, don't render (user must trigger manually)
      if (!autoShow) {
        return;
      }

      // Render React component (only if we should show it)
      this.render();

      // Track widget view (fire-and-forget)
      trackWidgetView(this.widgetId);

      // Handle delay if configured
      if (delaySeconds > 0) {
        this.close();
        setTimeout(() => {
          this.open();
          this.recordWidgetShown(triggerFrequency);
        }, delaySeconds * 1000);
      } else {
        this.recordWidgetShown(triggerFrequency);
      }
    } catch (error) {
      console.error('❌ Verivibe SDK: Initialization FAILED', {
        errorMessage: error instanceof Error ? error.message : String(error),
        widgetId: this.widgetId,
      });
    }
  }

  /**
   * Create container element for the widget
   */
  private createContainer(): HTMLElement {
    console.log('🔍 Verivibe SDK: Looking for existing container...', {
      containerId: this.containerId,
    });

    // Check if container already exists
    let container = document.getElementById(this.containerId);

    if (container) {
      console.log('♻️ Verivibe SDK: Found existing container', {
        containerId: this.containerId,
        container: container,
      });
    } else {
      console.log('🆕 Verivibe SDK: No existing container found, creating new one...', {
        containerId: this.containerId,
      });

      container = document.createElement('div');
      container.id = this.containerId;
      container.className = 'verivibe-widget-container';

      // Dynamic positioning based on widget config
      const position = this.widget?.config?.position || 'bottom-right';
      const positionStyles: Record<string, string> = {
        position: 'fixed',
        bottom: '24px',
        zIndex: '2147483647', // Maximum z-index
        pointerEvents: 'none', // Allow clicking through container
      };

      // Apply horizontal positioning
      if (position === 'bottom-left') {
        positionStyles.left = '24px';
        positionStyles.right = 'auto';
      } else if (position === 'center') {
        positionStyles.left = '50%';
        positionStyles.right = 'auto';
        positionStyles.transform = 'translateX(-50%)';
      } else {
        // bottom-right (default)
        positionStyles.right = '24px';
        positionStyles.left = 'auto';
      }

      Object.assign(container.style, positionStyles);

      console.log('📍 Verivibe SDK: Appending container to document.body...', {
        bodyExists: !!document.body,
        bodyChildren: document.body?.children.length,
        position,
      });

      document.body.appendChild(container);

      console.log('✅ Verivibe SDK: Container created and appended to body', {
        containerId: container.id,
        containerParent: container.parentElement?.tagName,
      });
    }

    return container;
  }

  /**
   * Inject Tailwind styles and custom CSS into Shadow DOM
   * Project styling is applied dynamically for live updates
   */
  private injectStyles(): void {
    if (!this.shadowRoot) return;

    // Inject base widget styles
    const styleElement = document.createElement('style');
    styleElement.textContent = widgetStyles;
    this.shadowRoot.appendChild(styleElement);

    // Generate CSS variables from project styling
    const projectStyles = this.generateProjectStyleVariables();
    if (projectStyles) {
      const projectStyleElement = document.createElement('style');
      projectStyleElement.id = 'verivibe-project-styles';
      projectStyleElement.textContent = projectStyles;
      this.shadowRoot.appendChild(projectStyleElement);
      console.log('🎨 Verivibe SDK: Project styling variables injected');
    }

    // Inject custom CSS from project if present (sanitized to block dangerous patterns)
    if (this.projectStyling?.custom_css) {
      const sanitizedCss = this.sanitizeCss(this.projectStyling.custom_css);
      if (sanitizedCss) {
        const customStyleElement = document.createElement('style');
        customStyleElement.id = 'verivibe-custom-css';
        customStyleElement.textContent = sanitizedCss;
        this.shadowRoot.appendChild(customStyleElement);
      }
    }
  }

  /**
   * Generate CSS variables from project styling for dynamic theming
   */
  private generateProjectStyleVariables(): string | null {
    if (!this.projectStyling) return null;

    const {
      brand_color = '#f59e0b',
      widget_padding = 16,
      widget_border_radius = 12,
    } = this.projectStyling;

    // Sanitize values to prevent CSS injection
    const safeBrandColor = this.sanitizeCssValue(String(brand_color));
    const safePadding = Math.max(0, Math.min(100, Number(widget_padding) || 16));
    const safeBorderRadius = Math.max(0, Math.min(100, Number(widget_border_radius) || 12));

    return `
      :host {
        --verivibe-brand-color: ${safeBrandColor};
        --verivibe-widget-padding: ${safePadding}px;
        --verivibe-widget-border-radius: ${safeBorderRadius}px;
      }

      /* Apply project styling to widget container */
      #verivibe-root {
        --brand-color: ${safeBrandColor};
      }
    `;
  }

  /**
   * Render React component inside Shadow DOM
   * Project styling is applied to customize widget appearance
   */
  private render(): void {
    if (!this.shadowRoot || !this.widget) return;

    const mountPoint = this.shadowRoot.getElementById('verivibe-root');
    if (!mountPoint) return;

    // Enable pointer events for the widget
    Object.assign(mountPoint.style, {
      pointerEvents: 'auto',
    });

    // Apply project styling to mount point container
    if (this.projectStyling) {
      const { widget_padding = 16, widget_border_radius = 12 } = this.projectStyling;
      Object.assign(mountPoint.style, {
        padding: `${widget_padding}px`,
        borderRadius: `${widget_border_radius}px`,
      });
    }

    // Enhance widget config with project brand color if available
    // This allows WidgetRenderer to use project styling for buttons/accents
    const enhancedWidget = {
      ...this.widget,
      config: {
        ...this.widget.config,
        // Use project brand color as widget color if not explicitly set
        color: this.widget.config.color || this.projectStyling?.brand_color || '#0070FF',
      },
    };

    this.reactRoot = createRoot(mountPoint);

    this.reactRoot.render(
      <React.StrictMode>
        <WidgetRenderer widget={enhancedWidget} previewMode={false} onSubmit={this.handleSubmit} />
      </React.StrictMode>
    );
  }

  /**
   * Handle feedback submission
   */
  private handleSubmit = async (feedback: {
    response_type: 'positive' | 'negative' | 'emoji_value' | 'email_capture' | 'audio';
    response_value: string | number;
    comment?: string;
    budget?: string;
    email?: string;
    path_taken?: 'positive' | 'negative';
    audio_blob?: Blob;
    is_audio_transcript?: boolean;
  }): Promise<void> => {
    if (!this.widget) return;

    const result = await submitVibe(this.widgetId, this.widget.project_id, feedback);

    if (!result.success) {
      console.error('Verivibe SDK: Submission failed', result.error);
    }
  };

  /**
   * Destroy the widget
   */
  destroy(): void {
    if (this.reactRoot) {
      this.reactRoot.unmount();
      this.reactRoot = null;
    }

    const container = document.getElementById(this.containerId);
    if (container) {
      container.remove();
    }

    this.shadowRoot = null;
    this.widget = null;
    this.projectStyling = null;
  }

  /**
   * Check if widget should be shown based on trigger frequency
   */
  private checkTriggerFrequency(frequency: string): boolean {
    if (frequency === 'always') {
      return true;
    }

    const storageKey = this.getStorageKey(frequency);

    if (frequency === 'once_per_session') {
      // Check sessionStorage - cleared when browser tab closes
      const shown = sessionStorage.getItem(storageKey);
      return !shown;
    }

    if (frequency === 'once_per_day') {
      // Check localStorage with date check
      const lastShown = localStorage.getItem(storageKey);
      if (!lastShown) return true;

      const lastShownDate = new Date(lastShown);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      lastShownDate.setHours(0, 0, 0, 0);

      return lastShownDate.getTime() < today.getTime();
    }

    if (frequency === 'once_per_user') {
      // Check if user is identified
      const currentUser = window.Verivibe?.getUser?.();
      if (!currentUser?.id) {
        console.log(
          '🚫 Verivibe SDK: once_per_user requires user identification. Call Verivibe.identify() first.'
        );
        return false;
      }

      const userStorageKey = `${storageKey}_${currentUser.id}`;
      const shown = localStorage.getItem(userStorageKey);
      return !shown;
    }

    if (frequency === 'once_per_ip') {
      // Use a stable browser fingerprint for device-based tracking
      // Note: This tracks per-device, not actual IP (IP tracking requires backend)
      const fingerprint = this.getBrowserFingerprint();
      const ipStorageKey = `${storageKey}_${fingerprint}`;
      const shown = localStorage.getItem(ipStorageKey);

      console.log('🔍 Verivibe SDK: Checking once_per_ip frequency', {
        alreadyShown: !!shown,
      });

      return !shown;
    }

    return true;
  }

  /**
   * Record that widget was shown
   */
  private recordWidgetShown(frequency: string): void {
    const storageKey = this.getStorageKey(frequency);

    if (frequency === 'once_per_session') {
      sessionStorage.setItem(storageKey, 'true');
    } else if (frequency === 'once_per_day') {
      localStorage.setItem(storageKey, new Date().toISOString());
    } else if (frequency === 'once_per_user') {
      const currentUser = window.Verivibe?.getUser?.();
      if (currentUser?.id) {
        const userStorageKey = `${storageKey}_${currentUser.id}`;
        localStorage.setItem(userStorageKey, 'true');
      }
    } else if (frequency === 'once_per_ip') {
      const fingerprint = this.getBrowserFingerprint();
      const ipStorageKey = `${storageKey}_${fingerprint}`;
      localStorage.setItem(ipStorageKey, 'true');
      console.log('📝 Verivibe SDK: Recorded widget shown for once_per_ip');
    }
  }

  /**
   * Get storage key for this widget and frequency type
   */
  private getStorageKey(frequency: string): string {
    return `verivibe_${this.widgetId}_${frequency}`;
  }

  /**
   * Generate a stable browser fingerprint for device-based tracking
   * Note: This is for "once per device" tracking, not actual IP tracking
   */
  private getBrowserFingerprint(): string {
    // Use stable browser properties only (no canvas which can be unreliable)
    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      screen.colorDepth,
      new Date().getTimezoneOffset(),
      navigator.hardwareConcurrency || 'unknown',
      navigator.maxTouchPoints || 0,
    ].join('|');

    // Simple hash function
    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }

    return Math.abs(hash).toString(36);
  }

  /**
   * Sanitize CSS to prevent injection attacks.
   * Blocks url(), @import, expression(), javascript:, and behavior properties
   * that could be used for data exfiltration or script execution.
   */
  private sanitizeCss(css: string): string {
    // Remove dangerous patterns that could exfiltrate data or execute code
    let sanitized = css;
    // Block url() calls (data exfiltration via CSS)
    sanitized = sanitized.replace(/url\s*\(/gi, '/* blocked-url */(');
    // Block @import rules
    sanitized = sanitized.replace(/@import\b/gi, '/* blocked-import */');
    // Block expression() (IE legacy script execution)
    sanitized = sanitized.replace(/expression\s*\(/gi, '/* blocked-expression */(');
    // Block javascript: protocol
    sanitized = sanitized.replace(/javascript\s*:/gi, '/* blocked */');
    // Block -moz-binding (Firefox XBL injection)
    sanitized = sanitized.replace(/-moz-binding\s*:/gi, '/* blocked */');
    // Block behavior property (IE HTC injection)
    sanitized = sanitized.replace(/behavior\s*:/gi, '/* blocked */');
    return sanitized;
  }

  /**
   * Validate a CSS value to prevent injection via CSS custom properties.
   * Only allows safe characters for colors, numbers, and units.
   */
  private sanitizeCssValue(value: string): string {
    // Strip anything that isn't alphanumeric, #, ., -, or whitespace
    return value.replace(/[^a-zA-Z0-9#.,\-\s%()]/g, '');
  }

  /**
   * Programmatic Control
   */
  open(): void {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('verivibe:open'));
    }
  }

  close(): void {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('verivibe:close'));
    }
  }
}

/**
 * Auto-initialize when script loads
 */
(function autoInit() {
  console.log('🎬 Verivibe SDK: Script loaded and executing', {
    readyState: document.readyState,
  });

  // Wait for window to fully load to ensure DOM is ready
  // This is crucial for page builders like Framer and Lovable
  if (document.readyState === 'complete') {
    console.log('⚡ Verivibe SDK: Document already complete, initializing immediately');
    initializeSDK();
  } else {
    console.log('⏳ Verivibe SDK: Document not ready, waiting for window.load event...', {
      readyState: document.readyState,
    });

    window.addEventListener('load', () => {
      console.log('✅ Verivibe SDK: Window load event fired, initializing now');
      initializeSDK();
    });

    // Fallback: Also listen to DOMContentLoaded in case load takes too long
    document.addEventListener('DOMContentLoaded', () => {
      console.log('📄 Verivibe SDK: DOMContentLoaded fired (fallback listener)');
    });
  }

  function initializeSDK() {
    console.log('🔧 Verivibe SDK: initializeSDK() called', {
      timestamp: new Date().toISOString(),
      readyState: document.readyState,
      bodyExists: !!document.body,
      bodyChildren: document.body?.children.length,
    });

    // Find the script tag
    console.log('🔍 Verivibe SDK: Looking for script tag...');
    const scriptTag =
      (document.currentScript as HTMLScriptElement) ||
      document.querySelector<HTMLScriptElement>('script[data-widget-id]');

    if (!scriptTag) {
      console.error(
        '❌ Verivibe SDK: Script tag NOT found. Ensure script has data-widget-id attribute.'
      );
      return;
    }

    console.log('✅ Verivibe SDK: Script tag found', {
      scriptSrc: scriptTag.src,
      scriptTag: scriptTag,
    });

    // Read configuration from data attributes
    const widgetId = scriptTag.getAttribute('data-widget-id');
    const siteId = scriptTag.getAttribute('data-site-id');
    const consentAttr = scriptTag.getAttribute('data-consent') as ConsentMode | null;

    console.log('📋 Verivibe SDK: Script tag attributes detected', {
      widgetId: widgetId,
      siteId: siteId,
      hasWidgetId: !!widgetId,
      hasSiteId: !!siteId,
    });

    if (!widgetId) {
      console.error('❌ Verivibe SDK: Missing required data-widget-id attribute on script tag.');
      return;
    }

    console.log('🎯 Verivibe SDK: Configuration valid, creating widget instance...', {
      widgetId,
      siteId,
    });

    // Initialize widget with consent mode
    const consentMode: ConsentMode = consentAttr === 'external' ? 'external' : 'none';
    const widget = new VerivibeWidget(widgetId, siteId, consentMode);

    console.log('🚀 Verivibe SDK: Calling widget.init()...');
    widget.init();

    // Widget instance is NOT exposed globally for security reasons
  }
})();

/**
 * Expose global SDK API
 */
if (typeof window !== 'undefined') {
  const extendedSDK = {
    ...VerivibeSDK,
    open: () => window.dispatchEvent(new CustomEvent('verivibe:open')),
    close: () => window.dispatchEvent(new CustomEvent('verivibe:close')),
    /**
     * Grant consent for data collection. Required when using data-consent="external".
     * Persists consent in localStorage and triggers deferred widget initialization.
     */
    grantConsent: () => {
      consentGranted = true;
      // Persist so consent survives page reloads
      const scriptTag = document.querySelector<HTMLScriptElement>('script[data-widget-id]');
      const wid = scriptTag?.getAttribute('data-widget-id');
      if (wid) localStorage.setItem(`verivibe_${wid}_consent`, 'granted');
      // Trigger deferred widget init if pending
      if (pendingWidgetInit) {
        pendingWidgetInit();
        pendingWidgetInit = null;
      }
    },
    /**
     * Revoke consent. Hides widget and clears all verivibe localStorage keys.
     */
    revokeConsent: () => {
      consentGranted = false;
      pendingWidgetInit = null;
      // Clear all verivibe keys from localStorage and sessionStorage
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('verivibe_')) keysToRemove.push(key);
      }
      keysToRemove.forEach(k => localStorage.removeItem(k));
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const key = sessionStorage.key(i);
        if (key?.startsWith('verivibe_')) sessionStorage.removeItem(key);
      }
      // Close the widget
      window.dispatchEvent(new CustomEvent('verivibe:close'));
    },
  };

  window.Verivibe = extendedSDK;

  // Also expose identify function globally for convenience
  window.verovibeIdentify = identifyUser;

  // Global Click Listener for [data-verivibe-trigger]
  document.addEventListener('click', e => {
    const target = e.target as HTMLElement;
    const triggerBtn = target.closest('[data-verivibe-trigger]');
    if (triggerBtn) {
      console.log('👆 Verivibe SDK: Trigger clicked');
      extendedSDK.open();
    }
  });
}

export default VerivibeWidget;
